/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useDataCarriers } from "../../store/useDataCarriers";

// Extend dayjs with weekOfYear plugin
dayjs.extend(weekOfYear);

export const DataCarrierGradePercentage = React.memo(() => {
  /** Refs */
  const snapshotRef = useRef(null);
  /** Hooks */
  const { dataCarrierGradePercentageStats, getDataCarrierGradePercentages } =
    useDataCarriers();
  const [_, takeScreenshot] = useScreenshot();
  /** State */
  const [yearRange, setYearRange] = useState(new Date());

  /** Callbacks */
  const downloadScreenshot = () => {
    takeScreenshot(snapshotRef.current).then((image: any) => {
      // Create a link to download the image
      const link = document.createElement("a");
      link.href = image;
      link.download = "data_carrier_grade_percentages.png"; // Set the default file name
      link.click(); // Programmatically click the link to trigger download
    });
  };

  const getLastYear = () =>
    setYearRange(dayjs(yearRange).subtract(1, "year").toDate());
  const getNextYear = () =>
    setYearRange(dayjs(yearRange).add(1, "year").toDate());

  /** Effects */
  useEffect(() => {
    getDataCarrierGradePercentages(yearRange);
  }, [yearRange]);

  return (
    <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-10">
      <div className="flex flex-col sm:flex-row w-full items-start justify-between">
        <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-2 sm:mb-12">
          {/* Volumes of the week */}
          Data carriers quality
        </p>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end">
          <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
            <PrimaryButton size="sm" onClick={getLastYear} label="Prev" />
            <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
              {dayjs(yearRange).format("YYYY")}
            </div>
            <PrimaryButton
              size="sm"
              disabled={dayjs(yearRange).add(1, "year").isAfter(dayjs())}
              onClick={getNextYear}
              label="Next"
            />
          </div>
          <div className="ml-0 sm:ml-2" />
          <PrimaryButton
            size="sm"
            onClick={downloadScreenshot}
            label="Download image"
          />
        </div>
      </div>
      <div ref={snapshotRef}>
        {dataCarrierGradePercentageStats?.length ? (
          <Chart
            // @ts-ignore
            options={{
              ...options,
              xaxis: {
                // type: "numeric",
                // tickAmount: dataCarrierGradePercentageStats[0].data
                //   // @ts-ignore
                //   .map((item) => String(item?.[0]))?.length,
                categories: dataCarrierGradePercentageStats[0].data
                  // @ts-ignore
                  .map((item) => item?.[0])
                  ?.sort(),
                labels: {
                  formatter: function (value) {
                    return dayjs(value).format("D MMM");
                  },
                },
              },
            }}
            series={dataCarrierGradePercentageStats?.map((item) => ({
              name: item.name,
              // @ts-ignore
              data: item.data?.map((item) => item?.[1]),
            }))}
            type="bar"
            height={300}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
              No data found
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

const options = {
  chart: {
    type: "bar",
    height: 300,
    stacked: true,
    stackType: "100%",
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
  },
  fill: {
    opacity: 1,
  },
  // xaxis: {
  //   labels: {
  //     show: false, // Hide x-axis labels
  //   },
  //   axisTicks: {
  //     show: false, // Hide axis ticks
  //   },
  //   axisBorder: {
  //     show: false, // Hide axis border
  //   },
  // },
  responsive: [
    {
      breakpoint: 480,
      options: {
        legend: {
          position: "bottom",
          offsetX: -10,
          offsetY: 0,
        },
      },
    },
  ],
  dataLabels: {
    enabled: false, // Enable data labels
    // formatter: function (val: number) {
    //   return val.toFixed(1) + "%";
    // },
    // style: {
    //   fontSize: "12px",
    // },
    // offsetY: -7,
  },
  tooltip: {
    theme: "dark",
    y: {
      formatter: (value: number) => {
        return `${value}%`;
      },
    },
  },
  yaxis: {
    labels: {
      formatter: function (val: number) {
        return val + "%";
      },
    },
    title: {
      text: "Percentage",
      style: {
        fontSize: "14px",
        fontWeight: 500,
        fontFamily: "satoshi",
      },
    },
  },
  grid: {
    strokeDashArray: 5,
  },
  legend: {
    position: "right",
  },
  markers: {
    size: 1,
  },
  colors: [
    "#23C16B", // Green
    "#FFB323", // Yellow
    "#48A7F8", // Blue
    "#FF3333", // Red
    "#FF8800", // Orange
    "#9259FF", // Purple
    "#FF4DA6", // Pink
    "#33FFE6", // Teal
    "#808080", // Gray
    "#D17A50", // Brown
    "#FF7F50", // Coral
    "#8A2BE2", // BlueViolet
    "#5F9EA0", // CadetBlue
    "#DC143C", // Crimson
    "#00FFFF", // Cyan
    "#FF1493", // DeepPink
    "#696969", // DimGray
    "#B8860B", // DarkGoldenrod
    "#FF4500", // OrangeRed
    "#32CD32", // LimeGreen
    "#4682B4", // SteelBlue
    "#00FA9A", // MediumSpringGreen
    "#FFD700", // Gold
    "#FF6347", // Tomato
    "#7B68EE", // MediumSlateBlue
    "#008080", // Teal
    "#6A5ACD", // SlateBlue
    "#D2691E", // Chocolate
    "#B22222", // FireBrick
    "#9ACD32", // YellowGreen
    "#8B008B", // DarkMagenta
    "#00CED1", // DarkTurquoise
    "#FF69B4", // HotPink
    "#1E90FF", // DodgerBlue
    "#40E0D0", // Turquoise
    "#EE82EE", // Violet
  ],
};
